/**
 * Utility functions for clipboard operations
 */

export interface ClipboardResult {
  success: boolean;
  error?: string;
}

/**
 * Copy text to clipboard using modern API with fallback
 * @param text The text to copy to clipboard
 * @returns Promise with success status and optional error message
 */
export async function copyToClipboard(text: string): Promise<ClipboardResult> {
  try {
    // Modern clipboard API (preferred)
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return { success: true };
    }
    
    // Fallback for older browsers or non-secure contexts
    return fallbackCopyToClipboard(text);
  } catch (error) {
    console.error('Clipboard copy failed:', error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Failed to copy to clipboard' 
    };
  }
}

/**
 * Fallback clipboard copy for older browsers
 * @param text The text to copy
 * @returns Promise with success status
 */
function fallbackCopyToClipboard(text: string): Promise<ClipboardResult> {
  return new Promise((resolve) => {
    try {
      // Create a temporary textarea element
      const textArea = document.createElement('textarea');
      textArea.value = text;
      
      // Make it invisible
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      textArea.setAttribute('readonly', '');
      
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      
      // Execute copy command
      const successful = document.execCommand('copy');
      document.body.removeChild(textArea);
      
      if (successful) {
        resolve({ success: true });
      } else {
        resolve({ 
          success: false, 
          error: 'Copy command failed - please copy manually' 
        });
      }
    } catch (error) {
      resolve({ 
        success: false, 
        error: error instanceof Error ? error.message : 'Copy operation failed' 
      });
    }
  });
}

/**
 * Check if clipboard API is available
 * @returns boolean indicating if clipboard write is supported
 */
export function isClipboardSupported(): boolean {
  return !!(navigator.clipboard && window.isSecureContext) || 
         document.queryCommandSupported?.('copy');
}